import { Badge, Button } from '@auctisync/ui';
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * Super-admin — reset a tenant's onboarding (spec §5, B3).
 *
 * Calls reset_tenant_onboarding(tenant): clears onboarding progress + any staged
 * bidder import and returns the tenant to publish_state='draft'. This is the
 * DTA dogfood control for re-running the onboarding flow cleanly.
 *
 * Destructive on a LIVE tenant — it removes them from public — so the reset is
 * gated behind a typed confirmation (the operator types the tenant's exact
 * name). The RPC itself is super-admin only (private.is_super_admin()).
 *
 * NOTE: reset does NOT un-materialize bidders already created by a prior go-live
 * (auth.users / tenant_buyers). It's a config re-run, not a full teardown.
 */

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  publish_state: string;
}

/** Typed bridge for reset_tenant_onboarding until database.ts regenerates. */
type ResetRpc = (
  fn: 'reset_tenant_onboarding',
  args: { p_tenant_id: string },
) => Promise<{ data: number | null; error: { message: string } | null }>;
const resetRpc = supabase.rpc.bind(supabase) as unknown as ResetRpc;

export function SuperOnboardingReset() {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState<{ id: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await supabase
      .from('tenants')
      .select('id, name, slug, publish_state')
      .order('name');
    if (res.error) {
      setError(res.error.message);
      setTenants(null);
    } else {
      setTenants((res.data ?? []) as unknown as TenantRow[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function startConfirm(id: string) {
    setConfirmingId(id);
    setConfirmText('');
    setResult(null);
    setError(null);
  }

  async function doReset(tenant: TenantRow) {
    if (confirmText.trim() !== tenant.name) return;
    setResetting(true);
    setError(null);
    const { data, error: rErr } = await resetRpc('reset_tenant_onboarding', {
      p_tenant_id: tenant.id,
    });
    setResetting(false);
    if (rErr) {
      setError(/super-admin/i.test(rErr.message) ? 'Super-admin only.' : rErr.message);
      return;
    }
    setConfirmingId(null);
    setConfirmText('');
    setResult({
      id: tenant.id,
      message: `Reset ${tenant.name} — cleared ${data ?? 0} step${data === 1 ? '' : 's'} and re-drafted.`,
    });
    void load();
  }

  return (
    <main style={{ padding: '2rem', maxWidth: '56rem', margin: '0 auto' }}>
      <h1
        style={{
          fontFamily: 'var(--font-heading-active)',
          fontSize: '1.75rem',
          fontWeight: 700,
          color: 'var(--text)',
          margin: 0,
        }}
      >
        Reset tenant onboarding
      </h1>
      <p style={{ color: 'var(--text-muted)', margin: '0.4rem 0 1.25rem', maxWidth: '42rem' }}>
        Clears a tenant&apos;s onboarding progress and any staged bidder import, and returns them to{' '}
        <strong>draft</strong> (removes a live tenant from public). Use it to re-run the onboarding
        flow — e.g. the DTA dogfood. Already-invited bidder accounts are not removed.
      </p>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading tenants…</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.6rem' }}>
          {(tenants ?? []).map((t) => (
            <li key={t.id} style={cardStyle}>
              <div
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{t.name}</span>{' '}
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>/{t.slug}</span>
                  <div style={{ marginTop: '0.3rem' }}>
                    <Badge variant={t.publish_state === 'live' ? 'success' : 'secondary'}>
                      {t.publish_state}
                    </Badge>
                  </div>
                </div>
                {confirmingId !== t.id && (
                  <Button variant="outline" onClick={() => startConfirm(t.id)}>
                    Reset onboarding
                  </Button>
                )}
              </div>

              {result?.id === t.id && (
                <p
                  style={{
                    color: 'var(--semantic-success)',
                    margin: '0.6rem 0 0',
                    fontSize: '0.9rem',
                  }}
                >
                  ✓ {result.message}
                </p>
              )}

              {confirmingId === t.id && (
                <div
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.85rem 1rem',
                    borderRadius: 'var(--r-tile, 0.375rem)',
                    background: 'var(--surface-danger, #fef2f2)',
                    border: '1px solid var(--border-danger, #fecaca)',
                  }}
                >
                  <p
                    style={{ margin: 0, color: 'var(--text-danger, #991b1b)', fontSize: '0.9rem' }}
                  >
                    {t.publish_state === 'live' ? (
                      <>
                        <strong>{t.name} is LIVE.</strong> Resetting re-drafts it and removes it
                        from public.{' '}
                      </>
                    ) : null}
                    To confirm, type the tenant name <strong>{t.name}</strong> below.
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      marginTop: '0.6rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={t.name}
                      disabled={resetting}
                      style={{
                        flex: 1,
                        minWidth: '12rem',
                        padding: '0.4rem 0.6rem',
                        borderRadius: 'var(--r-tile, 0.375rem)',
                        border: '1px solid var(--admin-hairline, var(--border))',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                    <Button
                      onClick={() => void doReset(t)}
                      disabled={resetting || confirmText.trim() !== t.name}
                    >
                      {resetting ? 'Resetting…' : 'Confirm reset'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmingId(null)}
                      disabled={resetting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
          {(tenants ?? []).length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>No tenants.</p>
          )}
        </ul>
      )}
    </main>
  );
}

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};
